import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-tax-cycle-workflow',
    templateUrl: './tax-cycle-workflow.component.html',
    styleUrls: ['./tax-cycle-workflow.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TaxCycleWorkflowComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  screenLabel = '';

  pageTitle = 'Tax Placemat';
  loading = false;
  showFilter = false;
  noData = false;
  percentile = 0;
  selectedYear: any = '';
  taxYears: Array<{ value: any; label: string }> = [];
  taxTypeDetails: any[] = [];
  taxTypeDropdownList: any[] = [];
  taxTypeDetailsSelection: any[] = [];
  data: any[] = [];
  filterData = '';
  actionsLoading = false;
  actionList: any[] = [];
  noAction = false;
  showSignoffModal = false;
  showUserAssignModal = false;
  showActionsModal = false;
  signoffStatus = 'N';
  selectedTaxUsers: any[] = [];
  assignUsers: any[] = [];
  userSearchText = '';
  userSuggestions: any[] = [];
  targetDate: any = null;
  commentKey = '';
  currentRow: any = null;
  currentMatItem: any = null;
  currentChildRow: any = null;
  private placematId: any = null;
  private isInitialized = false;

  @Input('base-urls')
  set _base_urls(baseUrls: string) {
    try {
      const urls = typeof baseUrls === 'string' ? safeJsonInput<any>(baseUrls, null, 'tax-cycle-workflow.parse1') : baseUrls;
      if (urls && Object.keys(urls).length > 0) {
        this.baseURLs = urls;
        this.tryInit();
      }
    } catch (e) {
      console.log('Input Not yet stable.');
    }
  }

  @Input('header-data')
  set _header_data(headerData: string) {
    try {
      const header = typeof headerData === 'string' ? safeJsonInput<any>(headerData, null, 'tax-cycle-workflow.parse2') : headerData;
      if (header && Object.keys(header).length > 0) {
        this.globalService = header.globalParams || {};
        this.USER_SETTINGS = header.userSettingsData || {};
        this.GENERAL_CONFIG = header.generalConfigData || {};
        this.tryInit();
      }
    } catch (e) {
      console.log('Input Not yet stable.');
    }
  }

  @Input('row-data')
  set _row_data(rowData: string) {
    try {
      const data = typeof rowData === 'string' ? safeJsonInput<any>(rowData, null, 'tax-cycle-workflow.parse3') : rowData;
      this.rowData = data || {};
    } catch (e) {
      console.log('Row data input not yet stable.');
    }
  }

  @Input('filter-params')
  set _filter_params(filterParams: string) {
    try {
      const params = typeof filterParams === 'string' ? safeJsonInput<any>(filterParams, null, 'tax-cycle-workflow.parse4') : filterParams;
      if (params && Object.keys(params).length > 0) {
        this.filterParams = params;
        this.selectedYear = params.tax_year ?? this.selectedYear;
        this.tryInit();
      }
    } catch (e) {
      console.log('Filter params input not yet stable.');
    }
  }

  @Input('screen-label')
  set _screen_label(label: string) {
    this.screenLabel = label || '';
  }

  constructor(private http: HttpClient, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.tryInit();
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }
    if (!this.GENERAL_CONFIG?.base_url) {
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    this.setupYears();
    this.loadTaxTypes();
  }

  private setupYears(): void {
    const currentYear = this.filterParams?.tax_year ?? this.globalService?.tax_year ?? '';
    this.selectedYear = currentYear;
    this.taxYears = currentYear ? [{ value: currentYear, label: String(currentYear) }] : [];
  }

  private loadTaxTypes(): void {
    this.loading = true;
    this.http.get<any>(`${this.GENERAL_CONFIG.base_url}/getTaxTypeData`, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response) => {
        const options = Array.isArray(response?.data) ? response.data : [];
        this.taxTypeDetails = options.map((item: any) => ({
          ...item,
          showCheckbox: true,
          role: 'checkbox',
        }));
        this.taxTypeDropdownList = [...this.taxTypeDetails];
        this.taxTypeDetailsSelection = [...this.taxTypeDetails];
        this.showFilter = true;
        this.loadPlacematList();
      },
      () => {
        this.loading = false;
        this.showFilter = true;
        this.noData = true;
        this.cdr.detectChanges();
      }
    );
  }

  onTaxTypeFilterResult(event: any): void {
    this.taxTypeDropdownList = event?.result || [];
  }

  private loadPlacematList(): void {
    const clientKey = this.USER_SETTINGS?.user?.client_key ?? this.globalService?.client_key ?? '';
    this.http.get<any>(`${this.GENERAL_CONFIG.base_url}/getTaxPlacematList?clientKey=${clientKey}`, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response) => {
        const placemats = Array.isArray(response?.data) ? response.data : [];
        const matched = placemats.find((p: any) => p?.placematName === this.screenLabel);
        const selected = matched || placemats[0] || {};
        this.placematId = selected.placematId ?? null;
        this.getData();
      },
      () => {
        this.loading = false;
        this.noData = true;
        this.cdr.detectChanges();
      }
    );
  }

  clearSearch(): void {
    this.filterData = '';
  }

  onTaxTypeToggle(item: any, checked: boolean): void {
    if (checked) {
      if (!this.taxTypeDetailsSelection.some((selected) => selected.id === item.id)) {
        this.taxTypeDetailsSelection.push(item);
      }
    } else {
      this.taxTypeDetailsSelection = this.taxTypeDetailsSelection.filter((selected) => selected.id !== item.id);
    }
  }

  isTaxTypeSelected(item: any): boolean {
    return this.taxTypeDetailsSelection.some((selected) => selected.id === item.id);
  }

  getData(): void {
    if (!this.placematId) {
      this.loading = false;
      this.noData = true;
      this.cdr.detectChanges();
      return;
    }
    const taxType = this.taxTypeDetailsSelection.map((item) => item.id).join(',');
    const clientKey = this.USER_SETTINGS?.user?.client_key ?? this.globalService?.client_key ?? '';
    this.loading = true;
    this.http.get<any>(
      `${this.GENERAL_CONFIG.base_url}/getTaxPlacematDetails?clientKey=${clientKey}&taxYear=${this.selectedYear}&taxType=${taxType}&placematId=${this.placematId}`,
      { withCredentials: true }
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response) => {
        this.data = Array.isArray(response?.data) ? response.data : [];
        this.noData = this.data.length === 0;
        this.calculatePercent();
        this.loading = false;
        this.cdr.detectChanges();
      },
      () => {
        this.data = [];
        this.noData = true;
        this.loading = false;
        this.cdr.detectChanges();
      }
    );
  }

  private calculatePercent(): void {
    let total = 0;
    let complete = 0;
    this.data.forEach((item: any) => {
      (item?.values || []).forEach((row: any) => {
        total += 1;
        if (row?.signoff_status === 'C') {
          complete += 1;
        }
      });
    });
    this.percentile = total > 0 ? Math.round((complete * 100) / total) : 0;
  }

  toggleRow(row: any): void {
    row.expanded = !row.expanded;
  }

  private dispatch(name: string, detail: any): void {
    const event = new CustomEvent(name, { detail, bubbles: true });
    const host = document.querySelector('gtw-tax-cycle-workflow-ce');
    if (host) {
      host.dispatchEvent(event);
    }
  }

  onCircleClick(event: Event, row: any, matItem: any): void {
    event.stopPropagation();
    this.currentRow = row;
    this.currentMatItem = matItem;
    this.signoffStatus = row?.signoff_status || 'N';
    this.selectedTaxUsers = Array.isArray(row?.userAssign) ? row.userAssign : [];
    this.showSignoffModal = true;
    this.cdr.detectChanges();
  }

  onUserClick(event: Event, row: any, matItem: any): void {
    event.stopPropagation();
    this.currentRow = row;
    this.currentMatItem = matItem;
    this.showUserAssignModal = true;
    this.assignUsers = [];
    this.targetDate = null;
    this.commentKey = '';
    this.loadUserAssignDetails();
  }

  onCrosshairClick(event: Event, row: any, matItem: any, childRow: any): void {
    event.stopPropagation();
    this.currentRow = row;
    this.currentMatItem = matItem;
    this.currentChildRow = childRow;
    this.openActionsModal(childRow);
  }

  onChildClick(row: any, childRow: any): void {
    this.dispatch('tax-cycle-workflow-go-to-screen', {
      activityKey: childRow?.activity_key,
      screenKey: childRow?.screen_key,
      toPlacematName: row?.to_placemat_name || ''
    });
  }

  closeSignoffModal(): void {
    this.showSignoffModal = false;
  }

  saveSignoffStatus(): void {
    if (!this.currentRow) {
      return;
    }
    const clientKey = this.USER_SETTINGS?.user?.client_key ?? '';
    const body = new URLSearchParams();
    body.set('activity_key', String(this.currentRow.activity_key ?? ''));
    body.set('client_key', String(clientKey));
    body.set('status', String(this.signoffStatus ?? 'N'));
    this.http.post<any>(`${this.GENERAL_CONFIG.admin_api_url}/updateSignOffStatus`, body.toString(), {
      withCredentials: true,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      () => {
        this.showSignoffModal = false;
        this.getData();
      },
      () => {
        this.showSignoffModal = false;
        this.cdr.detectChanges();
      }
    );
  }

  closeUserAssignModal(): void {
    this.showUserAssignModal = false;
  }

  private loadUserAssignDetails(): void {
    if (!this.currentRow) {
      return;
    }
    const clientKey = this.USER_SETTINGS?.user?.client_key ?? '';
    const body = new URLSearchParams();
    body.set('activity_key', String(this.currentRow.activity_key ?? ''));
    body.set('client_key', String(clientKey));
    this.http.post<any>(`${this.GENERAL_CONFIG.admin_api_url}/getActivityAssginDetailsByKey`, body.toString(), {
      withCredentials: true,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data) => {
        this.targetDate = data?.taskTargetDate ? new Date(data.taskTargetDate) : null;
        this.assignUsers = Array.isArray(data?.assign) ? data.assign : [];
        this.commentKey = data?.commentKey || '';
        this.cdr.detectChanges();
      },
      () => {
        this.assignUsers = [];
        this.cdr.detectChanges();
      }
    );
  }

  searchUsers(): void {
    const term = (this.userSearchText || '').trim();
    if (term.length < 2) {
      this.userSuggestions = [];
      return;
    }
    this.http.get<any>(`${this.GENERAL_CONFIG.base_url}/loadJsonObject?action_id=76&search_string=${encodeURIComponent(term)}`, {
      withCredentials: true
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response) => {
        this.userSuggestions = Array.isArray(response?.jsonObject) ? response.jsonObject : [];
        this.cdr.detectChanges();
      },
      () => {
        this.userSuggestions = [];
        this.cdr.detectChanges();
      }
    );
  }

  assignUser(user: any): void {
    if (!user) {
      return;
    }
    const exists = this.assignUsers.some((item: any) => (item.NAME || item.value) === (user.NAME || user.name));
    if (!exists) {
      this.assignUsers.push({
        NAME: user.NAME || user.name,
        VALUE: user.VALUE || user.value
      });
    }
    this.userSearchText = '';
    this.userSuggestions = [];
  }

  removeAssignedUser(user: any, index: number): void {
    if (index > -1) {
      this.assignUsers.splice(index, 1);
    }
    if (!this.currentRow) {
      return;
    }
    const clientKey = this.USER_SETTINGS?.user?.client_key ?? '';
    const body = new URLSearchParams();
    body.set('activity_key', String(this.currentRow.activity_key ?? ''));
    body.set('client_key', String(clientKey));
    body.set('ssoId', String(user?.VALUE || user?.key || ''));
    body.set('commentKey', String(this.commentKey || ''));
    this.http.post<any>(`${this.GENERAL_CONFIG.admin_api_url}/removeUserSignoff`, body.toString(), {
      withCredentials: true,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cdr.detectChanges());
  }

  saveUserAssignment(): void {
    if (!this.currentRow) {
      return;
    }
    const users = this.assignUsers.map((item: any) => ({ NAME: item.NAME || item.value, SSO_ID: item.VALUE || item.key }));
    const assign = users.map((item: any) => item.SSO_ID).join(',');
    const today = this.targetDate ? new Date(this.targetDate) : null;
    const taskObject: any = {
      state: '',
      filters: JSON.stringify([{ tax_year: this.selectedYear, tax_type: this.taxTypeDetailsSelection.map((v: any) => v.id).join(',') }]),
      tableState: '',
      data: JSON.stringify({
        process_id: this.currentRow.parentProcess_id || this.currentMatItem?.process_id,
        activity_key: this.currentRow.activity_key
      }),
      type: 'taxPlacemat',
      screen_key: this.filterParams?.screen_key || '',
      activity_key: this.filterParams?.activity_key || '',
      row_key: this.currentRow.parentProcess_id || this.currentMatItem?.process_id || '',
      col_key: this.currentRow.activity_key || '',
      element: '',
      comment_key: this.commentKey || '',
      is_public: 0,
      message: `This Action is Coming from TaxPlacemat ${this.currentRow.name || ''}`,
      notify: '',
      tags: '',
      value: '',
      subject: this.currentRow.name || '',
      task_action_item: 1,
      task_target_date: today
        ? `${today.getFullYear()}/${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}`
        : '',
      assign,
      tax_activity_key: this.currentRow.activity_key,
      source: 'T'
    };

    const taskUrl = this.commentKey
      ? `${this.GENERAL_CONFIG.base_url}/updateComment`
      : `${this.GENERAL_CONFIG.base_url}/addComment`;
    this.http.post<any>(taskUrl, taskObject, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      () => {
        const body = new URLSearchParams();
        body.set('activity_key', String(this.currentRow.activity_key ?? ''));
        body.set('client_key', String(this.USER_SETTINGS?.user?.client_key ?? ''));
        body.set('userlist', JSON.stringify(users));
        this.http.post<any>(`${this.GENERAL_CONFIG.admin_api_url}/activityUserSignoffAssign`, body.toString(), {
          withCredentials: true,
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.showUserAssignModal = false;
          this.getData();
        });
      },
      () => {
        this.showUserAssignModal = false;
        this.cdr.detectChanges();
      }
    );
  }

  openActionsModal(childRow: any): void {
    this.actionsLoading = true;
    this.noAction = false;
    this.showActionsModal = true;
    const screenKey = childRow?.screen_key ?? '';
    this.http.get<any>(`${this.GENERAL_CONFIG.base_url}/getGridActions?screen_key=${screenKey}`, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response) => {
        this.actionList = Array.isArray(response?.jsonObject) ? response.jsonObject : [];
        this.noAction = this.actionList.length === 0;
        this.actionsLoading = false;
        this.cdr.detectChanges();
      },
      () => {
        this.actionList = [];
        this.noAction = true;
        this.actionsLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  closeActionsModal(): void {
    this.showActionsModal = false;
  }

  onActionSelect(action: any): void {
    this.dispatch('tax-cycle-workflow-load-link', {
      actionTemplateKey: action?.action_template_key,
      childRow: this.currentChildRow
    });
    this.showActionsModal = false;
  }
}
